// Spoken descriptions for the live region. Pure: the translate function is
// injected so tests run without i18next. Squares are always named from
// White's side (algebraic), whatever the board orientation.
import { squareName } from '../game/squares.js';
import {
  EMPTY, FLAG_CASTLE_KING, FLAG_CASTLE_QUEEN, colorOf, typeOf, type Move, type Square,
} from '../game/types.js';

export type Translate = (key: string, vars?: Readonly<Record<string, string>>) => string;

const PIECE_KEYS = ['', 'pawn', 'knight', 'bishop', 'rook', 'queen', 'king'] as const;

function pieceName(t: Translate, code: number, set: 'pieces' | 'piecesTaken' = 'pieces'): string {
  return t(`narration.${set}.${PIECE_KEYS[typeOf(code)] ?? 'pawn'}`);
}

function sideName(t: Translate, code: number): string {
  return t(`narration.sides.${colorOf(code) === 'w' ? 'white' : 'black'}`);
}

/** "White: knight from g1 to f3. Check." */
export function narrateMove(t: Translate, move: Move, check: boolean): string {
  const side = sideName(t, move.piece);
  const parts: string[] = [];
  if ((move.flags & FLAG_CASTLE_KING) !== 0) parts.push(t('narration.castleKing', { side }));
  else if ((move.flags & FLAG_CASTLE_QUEEN) !== 0) parts.push(t('narration.castleQueen', { side }));
  else {
    const vars = { side, piece: pieceName(t, move.piece), from: squareName(move.from), to: squareName(move.to) };
    parts.push(move.captured === EMPTY
      ? t('narration.move', vars)
      : t('narration.capture', { ...vars, captured: pieceName(t, move.captured, 'piecesTaken') }));
  }
  if (move.promotion !== EMPTY) parts.push(t('narration.promotion', { piece: pieceName(t, move.promotion) }));
  if (check) parts.push(t('narration.check'));
  return parts.join(' ');
}

/** What the keyboard cursor is on: a piece, an empty square, or a legal target. */
export function narrateCursor(t: Translate, sq: Square, code: number, target: 'none' | 'move' | 'capture'): string {
  const square = squareName(sq);
  if (target === 'capture') return t('narration.cursor.capture', { square, piece: pieceName(t, code) });
  if (target === 'move') return t('narration.cursor.legal', { square });
  if (code === EMPTY) return t('narration.cursor.empty', { square });
  return t('narration.cursor.piece', { square, side: sideName(t, code).toLowerCase(), piece: pieceName(t, code) });
}

export function narrateSelection(t: Translate, sq: Square | null, code: number): string {
  if (sq === null) return t('narration.deselected');
  return t('narration.selected', { piece: pieceName(t, code), square: squareName(sq) });
}
